import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, Alert, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Chip, Divider
} from '@mui/material';
import { Home, Add, Edit, ListAlt, HomeWork } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { fmt, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const DEPR_METHODS = ['Straight-Line', 'Declining-Balance 1', 'Declining-Balance 2', 'DB1/SL', 'DB2/SL', 'Manual'];

const emptyForm = {
  faName: '', faClassCode: '', faSubclassCode: '', faPostingGroup: '',
  faLocationCode: '', description: '', responsiblePerson: '', serialNo: '',
  depreciationMethod: 'Straight-Line', depreciationStartingDate: '',
  noOfDepreciationYears: '', straightLinePercent: '', decliningBalancePercent: '', salvageValue: ''
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const FixedAssetList = () => {
  const navigate = useNavigate();
  const [assets, setAssets]               = useState([]);
  const [postingGroups, setPostingGroups] = useState([]);
  const [classes, setClasses]             = useState([]);
  const [subclasses, setSubclasses]       = useState([]);
  const [locations, setLocations]         = useState([]);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');
  const [loading, setLoading]             = useState(true);
  const [viewAsset, setViewAsset]         = useState(null);
  const [dialogOpen, setDialogOpen]       = useState(false);
  const [editingNo, setEditingNo]         = useState(null);
  const [form, setForm]                   = useState(emptyForm);
  const [formError, setFormError]         = useState(''); // shown INSIDE the create/edit dialog
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving, setSaving]               = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchAll = async () => {
    try {
      const [aRes, pgRes, clRes, scRes, locRes] = await Promise.all([
        axios.get(`${API}/api/fixed-assets`,                  { headers }),
        axios.get(`${API}/api/fixed-assets/posting-groups`,   { headers }),
        axios.get(`${API}/api/fixed-assets/classes`,          { headers }),
        axios.get(`${API}/api/fixed-assets/subclasses`,       { headers }),
        axios.get(`${API}/api/fixed-assets/locations`,        { headers }),
      ]);
      setAssets(aRes.data);
      setPostingGroups(pgRes.data);
      setClasses(clRes.data);
      setSubclasses(scRes.data);
      setLocations(locRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load fixed assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2000);
    return () => clearTimeout(t);
  }, [success]);

  const filteredSubclasses = form.faClassCode
    ? subclasses.filter(s => s.classCode === form.faClassCode)
    : subclasses;

  const handleCreate = () => { setEditingNo(null); setForm(emptyForm); setFormError(''); setDialogOpen(true); };

  const handleEdit = async (faNo) => {
    try {
      const r = await axios.get(`${API}/api/fixed-assets/${faNo}`, { headers });
      const a = r.data;
      const db = a.depreciationBook || {};
      setEditingNo(faNo);
      setForm({
        faName:                  a.faName || '',
        faClassCode:             a.faClassCode || '',
        faSubclassCode:          a.faSubclassCode || '',
        faPostingGroup:          a.faPostingGroup || '',
        faLocationCode:          a.faLocationCode || '',
        description:             a.description || '',
        responsiblePerson:       a.responsiblePerson || '',
        serialNo:                a.serialNo || '',
        depreciationMethod:      db.depreciationMethod || 'Straight-Line',
        depreciationStartingDate: db.depreciationStartingDate ? db.depreciationStartingDate.substring(0, 10) : '',
        noOfDepreciationYears:   db.noOfDepreciationYears || '',
        straightLinePercent:     db.straightLinePercent || '',
        decliningBalancePercent: db.decliningBalancePercent || '',
        salvageValue:            db.salvageValue || ''
      });
      setFormError('');
      setDialogOpen(true);
    } catch (err) {
      setError('Failed to load asset details');
    }
  };

  const handleSave = async () => {
    // Client-side checks so the user sees the reason immediately, in the dialog.
    if (!form.faName.trim())      return setFormError('Asset Name is required');
    if (!form.faPostingGroup)     return setFormError('FA Posting Group is required');
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        ...form,
        noOfDepreciationYears:   parseFloat(form.noOfDepreciationYears)   || 0,
        straightLinePercent:     parseFloat(form.straightLinePercent)     || 0,
        decliningBalancePercent: parseFloat(form.decliningBalancePercent) || 0,
        salvageValue:            parseFloat(form.salvageValue)            || 0,
      };
      if (editingNo) {
        await axios.put(`${API}/api/fixed-assets/${editingNo}`, payload, { headers });
        setSuccess('Fixed Asset updated');
      } else {
        await axios.post(`${API}/api/fixed-assets`, payload, { headers });
        setSuccess('Fixed Asset created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => {
    const val = e.target.value;
    setForm(f => ({
      ...f,
      [key]: val,
      ...(key === 'faClassCode' ? { faSubclassCode: '' } : {})
    }));
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <HomeWork sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Fixed Assets</Typography>
            </Box>
            <Box>
              <Button size="small" variant="outlined" sx={{ mr: 1 }}
                onClick={() => navigate('/fixed-assets/posting-groups')}>
                Posting Groups
              </Button>
              <Button size="small" variant="outlined" sx={{ mr: 1 }}
                onClick={() => navigate('/fixed-assets/classes')}>
                FA Classes
              </Button>
              <IconButton onClick={() => navigate('/dashboard')} title="Dashboard"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={handleCreate} title="New Fixed Asset"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {assets.length === 0 ? (
            <Typography color="text.secondary">No fixed assets yet. Create one to get started.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>FA No.</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Class / Subclass</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Posting Group</strong></TableCell>
                  <TableCell><strong>Depr. Method</strong></TableCell>
                  <TableCell align="right"><strong>Acq. Cost</strong></TableCell>
                  <TableCell align="right"><strong>Accum. Depr.</strong></TableCell>
                  <TableCell align="right"><strong>Book Value</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map(a => (
                  <TableRow key={a.faNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewAsset(a)}>
                    <TableCell><strong>{a.faNo}</strong></TableCell>
                    <TableCell>{a.faName}</TableCell>
                    <TableCell>
                      {a.faClassCode && <Chip label={a.faClassCode} size="small" variant="outlined" sx={{ mr: 0.5 }} />}
                      {a.faSubclassCode && <Chip label={a.faSubclassCode} size="small" color="primary" variant="outlined" />}
                    </TableCell>
                    <TableCell><Typography variant="caption">{a.faLocationCode || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{a.faPostingGroup}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{a.depreciationMethod || 'Straight-Line'}</Typography></TableCell>
                    <TableCell align="right">{(a.acquisitionCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {(a.accumulatedDepreciation || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Chip
                        label={(a.bookValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        color="success" size="small" variant="outlined"
                        onClick={() => navigate(`/fixed-assets/${a.faNo}/ledger`)}
                        sx={{ cursor: 'pointer', fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => handleEdit(a.faNo)} title="Edit"><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => navigate(`/fixed-assets/${a.faNo}/ledger`)} title="Ledger"><ListAlt fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── FA Card (view) ── */}
      <Dialog open={Boolean(viewAsset)} onClose={() => setViewAsset(null)} maxWidth="sm" fullWidth>
        {viewAsset && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <HomeWork color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewAsset.faName}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewAsset.faNo}</Typography>
                </Box>
                <Box ml="auto" display="flex" gap={0.5}>
                  {viewAsset.blocked && <Chip label="Blocked" color="error" size="small" />}
                  {viewAsset.isDisposed && <Chip label="Disposed" color="default" size="small" />}
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Identity</Typography>
              <InfoRow label="FA No."            value={viewAsset.faNo} />
              <InfoRow label="Serial No."        value={viewAsset.serialNo} />
              <InfoRow label="Responsible Person" value={viewAsset.responsiblePerson} />
              <InfoRow label="Description"       value={viewAsset.description} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Classification</Typography>
              <InfoRow label="FA Class"        value={viewAsset.faClassCode} />
              <InfoRow label="FA Subclass"     value={viewAsset.faSubclassCode} />
              <InfoRow label="Posting Group"   value={viewAsset.faPostingGroup} />
              <InfoRow label="Location"        value={viewAsset.faLocationCode} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Depreciation</Typography>
              <InfoRow label="Method"          value={viewAsset.depreciationMethod || 'Straight-Line'} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Book Values ({sym()})</Typography>
              <InfoRow label="Acquisition Cost"
                value={(viewAsset.acquisitionCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} />
              <InfoRow label="Accum. Depreciation"
                value={(viewAsset.accumulatedDepreciation || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} />
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>
                  Book Value
                </Typography>
                <Chip
                  label={`${sym()} ${fmt(viewAsset.bookValue || 0)}`}
                  color="success" size="small" variant="outlined"
                  onClick={() => { setViewAsset(null); navigate(`/fixed-assets/${viewAsset.faNo}/ledger`); }}
                  sx={{ cursor: 'pointer', fontWeight: 700 }}
                />
                <Typography variant="caption" color="text.secondary">(click to view ledger)</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button startIcon={<ListAlt />}
                onClick={() => { setViewAsset(null); navigate(`/fixed-assets/${viewAsset.faNo}/ledger`); }}>
                FA Ledger
              </Button>
              <Button startIcon={<Edit />}
                onClick={() => { setViewAsset(null); handleEdit(viewAsset.faNo); }}>
                Edit
              </Button>
              <Button variant="outlined" onClick={() => setViewAsset(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create / Edit Dialog */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingNo ? `Edit — ${editingNo}` : 'New Fixed Asset'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>

          {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>{formError}</Alert>}

          <TextField label="Asset Name *" value={form.faName} onChange={set('faName')}
            fullWidth size="small" sx={{ mb: 2 }} />

          <TextField select label="FA Posting Group *" value={form.faPostingGroup} onChange={set('faPostingGroup')}
            fullWidth size="small" sx={{ mb: 2 }}>
            {postingGroups.length === 0
              ? <MenuItem disabled value="">No posting groups — create one first</MenuItem>
              : postingGroups.map(pg => (
                  <MenuItem key={pg.groupCode} value={pg.groupCode}>{pg.groupCode} — {pg.description}</MenuItem>
                ))
            }
          </TextField>

          <Box display="flex" gap={2} mb={2}>
            <TextField select label="FA Class" value={form.faClassCode} onChange={set('faClassCode')}
              fullWidth size="small">
              <MenuItem value=""><em>— None —</em></MenuItem>
              {classes.map(c => (
                <MenuItem key={c.classCode} value={c.classCode}>{c.classCode} — {c.className}</MenuItem>
              ))}
            </TextField>

            <TextField select label="FA Subclass" value={form.faSubclassCode} onChange={set('faSubclassCode')}
              fullWidth size="small" disabled={!form.faClassCode}>
              <MenuItem value=""><em>— None —</em></MenuItem>
              {filteredSubclasses.map(s => (
                <MenuItem key={s.subclassCode} value={s.subclassCode}>{s.subclassCode} — {s.subclassName}</MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField select label="Location" value={form.faLocationCode} onChange={set('faLocationCode')}
            fullWidth size="small" sx={{ mb: 2 }}>
            <MenuItem value=""><em>— None —</em></MenuItem>
            {locations.map(l => (
              <MenuItem key={l.locationCode} value={l.locationCode}>{l.locationCode} — {l.locationName}</MenuItem>
            ))}
          </TextField>

          <Box display="flex" gap={2} mb={2}>
            <TextField label="Serial No." value={form.serialNo} onChange={set('serialNo')} fullWidth size="small" />
            <TextField label="Responsible Person" value={form.responsiblePerson} onChange={set('responsiblePerson')} fullWidth size="small" />
          </Box>

          <TextField label="Description" value={form.description} onChange={set('description')}
            fullWidth size="small" sx={{ mb: 2 }} />

          <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Depreciation Setup</Typography></Divider>

          <TextField select label="Depreciation Method" value={form.depreciationMethod} onChange={set('depreciationMethod')}
            fullWidth size="small" sx={{ mb: 2 }}>
            {DEPR_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>

          <TextField label="Depreciation Start Date" type="date" value={form.depreciationStartingDate}
            onChange={set('depreciationStartingDate')} fullWidth size="small" sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }} />

          <Box display="flex" gap={2} mb={2}>
            <TextField label="Useful Life (Years)" type="number" value={form.noOfDepreciationYears}
              onChange={set('noOfDepreciationYears')} fullWidth size="small" />
            <TextField label={`Salvage Value (${sym()})`} type="number" value={form.salvageValue}
              onChange={set('salvageValue')} fullWidth size="small" />
          </Box>

          {['Straight-Line', 'DB1/SL', 'DB2/SL'].includes(form.depreciationMethod) && (
            <TextField label="Straight-Line %" type="number" value={form.straightLinePercent}
              onChange={set('straightLinePercent')} fullWidth size="small" sx={{ mb: 2 }} />
          )}
          {['Declining-Balance 1', 'Declining-Balance 2', 'DB1/SL', 'DB2/SL'].includes(form.depreciationMethod) && (
            <TextField label="Declining Balance %" type="number" value={form.decliningBalancePercent}
              onChange={set('decliningBalancePercent')} fullWidth size="small" sx={{ mb: 2 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssetList;
